import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  size,
  useFloating,
  useMergeRefs,
} from '@floating-ui/react';
import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useClickOutside } from 'shared/hooks/use-click-outside';
import { useElectron } from 'shared/hooks/use-is-mac';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { InteractionType } from 'shared/types/data-type';

import * as Styled from './popup-menu.style';

export type PopupMenuProps = {
  children: React.ReactNode;
  referenceElement?: React.RefObject<HTMLElement>;
  location?: { top: number; left: number };
  position?: Placement;
  fallbackPositions?: Placement[];
  preventReferenceElementOverlap?: boolean;
  hasParentWidth?: boolean;
  onOutside?: () => void;
  className?: string;
  interactionType?: InteractionType;
  noBackdrop?: boolean;
  width?: number;
  ignoreSelector?: string;
  disableAutoUpdate?: boolean;
  /**
   * Focused once the menu is positioned (visible). Focusing on mount fails
   * because the container is `visibility: hidden` until floating-ui positions
   * it — a search input must wait for that transition.
   */
  initialFocus?: React.RefObject<HTMLElement | null>;
  focusOnOpen?: boolean;
};

// Only the most recently opened PopupMenu should react to Escape, so a nested
// menu (e.g. a select dropdown inside the filter panel) closes without also
// closing its parent.
const escapeStack: number[] = [];
let menuIdCounter = 0;

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const getFocusableElements = (container: HTMLElement | null): HTMLElement[] => {
  if (!container) {
    return [];
  }
  return Array.from(
    container.querySelectorAll<HTMLElement>(focusableSelector),
  ).filter((element) => element.offsetParent !== null);
};

const fallbackPlacements: Placement[] = [
  'right-end',
  'right-start',
  'bottom-end',
  'bottom-start',
  'left-start',
  'left-end',
  'top-end',
  'top-start',
];

export const PopupMenu = forwardRef<HTMLDivElement, PopupMenuProps>(
  (
    {
      children,
      referenceElement,
      location,
      position = 'bottom-start',
      fallbackPositions = fallbackPlacements,
      preventReferenceElementOverlap,
      hasParentWidth,
      onOutside,
      className,
      interactionType,
      noBackdrop,
      width,
      ignoreSelector,
      disableAutoUpdate,
      initialFocus,
      focusOnOpen,
    },
    ref,
  ) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const { isMac } = useElectron();
    const menuIdRef = useRef<number>(-1);

    const { refs, floatingStyles, isPositioned } = useFloating({
      elements: {
        reference: referenceElement?.current,
      },
      placement: position,
      whileElementsMounted: disableAutoUpdate ? undefined : autoUpdate,
      strategy: 'fixed',
      middleware: [
        offset({ mainAxis: 8 }),
        shift({
          padding: isMac
            ? {
                top: Styled.positioningOffset * 2,
                bottom: Styled.positioningOffset * 2,
                left: Styled.positioningOffset,
                right: Styled.positioningOffset,
              }
            : Styled.positioningOffset,
          crossAxis: !preventReferenceElementOverlap,
        }),
        flip({
          fallbackPlacements: fallbackPositions,
        }),
        hasParentWidth
          ? size({
              apply: ({ rects, elements }) => {
                elements.floating.style.width = `${rects.reference.width}px`;
              },
            })
          : undefined,
      ],
    });

    const clickOutside = (e: React.MouseEvent | MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onOutside?.();
    };
    const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

    useKeysDown(
      ['Escape'],
      (e) => {
        if (escapeStack[escapeStack.length - 1] !== menuIdRef.current) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        onOutside?.();
      },
      { ignoreInputFields: false, capture: true },
    );

    useEffect(() => {
      menuIdRef.current = menuIdCounter;
      menuIdCounter += 1;
      escapeStack.push(menuIdRef.current);
      return () => {
        const index = escapeStack.indexOf(menuIdRef.current);
        if (index !== -1) {
          escapeStack.splice(index, 1);
        }
      };
    }, []);

    const floatingRef = useMergeRefs([refs.setFloating, menuRef, ref]);

    useClickOutside(
      menuRef,
      noBackdrop ? clickOutside : undefined,
      ignoreSelector,
    );

    // Passive effects run after paint — the menu would flash at the viewport origin.
    useLayoutEffect(() => {
      if (!location) {
        return;
      }

      refs.setPositionReference({
        getBoundingClientRect: () => ({
          width: 0,
          height: 0,
          x: location.left,
          y: location.top,
          top: location.top,
          right: location.left,
          bottom: location.top,
          left: location.left,
        }),
      });
    }, [location, refs]);

    useEffect(() => {
      if (isPositioned) {
        initialFocus?.current?.focus();
      }
    }, [isPositioned, initialFocus]);

    // When opened as a focus-managed surface, move focus in and restore it to
    // whatever was focused before (the trigger) on close.
    useEffect(() => {
      if (!focusOnOpen) {
        return;
      }
      const previouslyFocused = document.activeElement as HTMLElement | null;
      // an explicit target owns the initial focus; grabbing the container here would
      // race it, and only the container survives when positioning already finished
      !initialFocus && menuRef.current?.focus();
      return () => {
        previouslyFocused?.focus?.();
      };
    }, [focusOnOpen, initialFocus, menuRef]);

    const trapFocus = (e: React.KeyboardEvent) => {
      if (!focusOnOpen || e.key !== 'Tab') {
        return;
      }
      const focusable = getFocusableElements(menuRef.current);
      if (!focusable.length) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || active === menuRef.current)) {
        e.preventDefault();
        last.focus();
        return;
      }
      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    return createPortal(
      <>
        {!noBackdrop && (
          <Styled.Backdrop
            onMouseDown={clickOutside}
            onContextMenu={clickOutside}
          />
        )}
        <Styled.Container
          $width={width}
          className={className}
          style={floatingStyles}
          ref={floatingRef}
          $isMac={isMac}
          tabIndex={focusOnOpen ? -1 : undefined}
          $positioned={isPositioned}
          data-no-dnd="true"
          data-interaction-for-type={interactionType}
          onClick={stopPropagation}
          onKeyDown={trapFocus}
        >
          <Styled.ScrollContainer>{children}</Styled.ScrollContainer>
        </Styled.Container>
      </>,
      document.body,
    );
  },
);
